// @need-translate
import {useCallback} from 'react';
import {t} from '../../../../../../common/getTranslations';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {QueryFromPage} from '../../../../../../graphql/generated/types';
import {trackEvent} from '../../../../../../logging/trackEvent';
import {currentEventContext} from '../../../../../../logging/variables';
import {ONLY_DATA_CONFIG} from '../../../../../../reactQuery/constant';
import {showToast} from '../../../../../../toast/toastHandler';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {useBuyTicketFlow} from '../../../../../buy/ticket/hook/useBuyTicketFlow';

export function useViewerMenuHandler({
  isMobile,
  isShortsDrama,
}: {
  isMobile: boolean;
  isShortsDrama?: boolean;
}) {
  const {seriesId, productId, isNonstopWatching} = useTypedSelector(state => {
    const productId = state.viewerInfo.currentProductId;
    const seriesId = state.viewerInfo.currentSeriesId;
    const isNonstopWatching =
      seriesId !== undefined
        ? !!state.viewerInfo.nonstopWatchingInSeries[seriesId]
            ?.isNonstopWatching
        : false;

    return {
      seriesId,
      productId,
      isNonstopWatching,
    };
  });
  const result = useViewerInfoQuery(
    // @ts-ignore
    {seriesId, productId},
    ONLY_DATA_CONFIG
  );
  const viewerInfoData = result.data;
  const nextItem = viewerInfoData?.nextItem;
  const prevItem = viewerInfoData?.prevItem;
  const shouldUseNonstopWatching = isNonstopWatching && nextItem;

  const {startTicketProcess} = useBuyTicketFlow({disableCommand: true});
  const page_meta = currentEventContext.currentPageView?.page_meta;

  const handleMoveNextProduct = useCallback(() => {
    if (!seriesId) {
      return;
    }

    if (!nextItem?.productId) {
      showToast({
        message: isShortsDrama
          ? t('viewer_menu_last_episode')
          : '다음편 정보가 없습니다',
        isMobile,
      });
      return;
    }

    if (isShortsDrama) {
      trackEvent({
        section: '뷰어',
        page: '영상뷰어',
        actionType: 'Event',
        actionName: '다음편_클릭',
        click: {
          layer1: '뷰어메뉴',
          layer2: '재생컨트롤러',
        },
        customProps: {
          user_action: 'click',
          is_shortdrama: isShortsDrama ? 'y' : undefined,
        },
        eventMeta: {
          id: String(nextItem.productId),
          type: 'productId',
        },
        ...(page_meta && {page_meta}),
      });
    }

    startTicketProcess({
      seriesId,
      currentProductId: productId,
      targetProductId: nextItem.productId,
      nonstopWatching: isNonstopWatching,
      queryFrom: QueryFromPage.Viewer,
      pickExactly: true,
    });
  }, [
    seriesId,
    productId,
    nextItem,
    startTicketProcess,
    isNonstopWatching,
    isMobile,
    isShortsDrama,
  ]);

  const handleMovePrevProduct = useCallback(() => {
    if (!seriesId || (isShortsDrama && !prevItem?.productId)) {
      return;
    }

    if (!prevItem?.productId) {
      showToast({
        message: '이전편 정보가 없습니다',
        isMobile,
      });
      return;
    }

    if (isShortsDrama) {
      trackEvent({
        section: '뷰어',
        page: '영상뷰어',
        actionType: 'Event',
        actionName: '이전편_클릭',
        click: {
          layer1: '뷰어메뉴',
          layer2: '재생컨트롤러',
        },
        customProps: {
          user_action: 'click',
          is_shortdrama: isShortsDrama ? 'y' : undefined,
        },
        eventMeta: {
          id: String(prevItem.productId),
          type: 'productId',
        },
        ...(page_meta && {page_meta}),
      });
    }

    startTicketProcess({
      seriesId,
      currentProductId: productId,
      targetProductId: prevItem.productId,
      nonstopWatching: isNonstopWatching,
      queryFrom: QueryFromPage.Viewer,
      pickExactly: true,
    });
  }, [
    seriesId,
    productId,
    prevItem,
    startTicketProcess,
    isNonstopWatching,
    isMobile,
    isShortsDrama,
  ]);

  return {
    shouldUseNonstopWatching,
    isNonstopWatching,
    handleMoveNextProduct,
    handleMovePrevProduct,
  };
}
